import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioApi } from '../services/api';
import { toast } from 'sonner';
import { Search, ArrowLeft, MapPin, Clock, Eye, X } from 'lucide-react';

const PROFESSIONS = [
  'Engineering', 'Design', 'Medicine & Healthcare',
  'Business & Management', 'Education & Teaching',
  'Law', 'Architecture', 'Arts & Creative',
  'Science & Research', 'Finance & Accounting',
  'Marketing & Media', 'Hospitality & Tourism',
  'Agriculture', 'Other'
];

export default function FindPeople() {
  const navigate = useNavigate();
  const [allPortfolios, setAllPortfolios] = useState([]);
  const [filteredPortfolios, setFilteredPortfolios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({
    activelyLooking: false,
    location: '',
    minExperience: 0,
    profession: '',
  });

  const loadAllPortfolios = useCallback(async () => {
    try {
      const { data } = await portfolioApi.search(0, 500);
      setAllPortfolios(data);
      setFilteredPortfolios(data);
    } catch (error) {
      console.error('Failed to load portfolios:', error);
      toast.error('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterPortfolios = useCallback(() => {
    let filtered = [...allPortfolios];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.title?.toLowerCase().includes(query) ||
        p.profession?.toLowerCase().includes(query) ||
        p.branch?.toLowerCase().includes(query) ||
        p.skills?.some(skill => skill.toLowerCase().includes(query)) ||
        p.location?.toLowerCase().includes(query)
      );
    }

    if (selectedFilters.activelyLooking) {
      filtered = filtered.filter(p => p.actively_looking);
    }

    if (selectedFilters.location.trim()) {
      const loc = selectedFilters.location.toLowerCase();
      filtered = filtered.filter(p => p.location?.toLowerCase().includes(loc));
    }

    if (selectedFilters.minExperience > 0) {
      filtered = filtered.filter(p =>
        p.years_experience && p.years_experience >= selectedFilters.minExperience
      );
    }

    if (selectedFilters.profession) {
      filtered = filtered.filter(p => p.profession === selectedFilters.profession);
    }

    setFilteredPortfolios(filtered);
  }, [searchQuery, selectedFilters, allPortfolios]);

  useEffect(() => { loadAllPortfolios(); }, [loadAllPortfolios]);
  useEffect(() => { filterPortfolios(); }, [filterPortfolios]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedFilters({ activelyLooking: false, location: '', minExperience: 0, profession: '' });
  };

  const hasActiveFilters = searchQuery || selectedFilters.activelyLooking ||
    selectedFilters.location || selectedFilters.minExperience > 0 || selectedFilters.profession;

  // Navigate to Browse starting at the clicked portfolio
  const handleViewProfile = (e, portfolio) => {
    e.stopPropagation();
    // Find the index of this portfolio in the full list so Browse can start there
    const indexInAll = allPortfolios.findIndex(p => p.id === portfolio.id);
    navigate("/browse", {
    state: { startPortfolioId: portfolio.id }
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#f5f1ed]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8744f] mx-auto mb-4" />
        <p className="text-gray-500 animate-pulse">Loading portfolios...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f1ed]">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/p/${portfolio.id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Home</span>
        </button>
        <span className="font-bold text-[#e8744f]">🔍 Find People</span>
        <span className="text-xs text-gray-500">{filteredPortfolios.length} results</span>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">

        {/* Search + Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, title, skill, profession, or location..."
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#e8744f] focus:outline-none focus:ring-2 focus:ring-orange-100 transition"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={selectedFilters.profession}
              onChange={e => setSelectedFilters(f => ({ ...f, profession: e.target.value }))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-xs focus:border-[#e8744f] focus:outline-none transition"
            >
              <option value="">All Professions</option>
              {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            <select
              value={selectedFilters.minExperience}
              onChange={e => setSelectedFilters(f => ({ ...f, minExperience: parseInt(e.target.value) }))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-xs focus:border-[#e8744f] focus:outline-none transition"
            >
              <option value="0">Any Experience</option>
              <option value="1">1+ years</option>
              <option value="3">3+ years</option>
              <option value="5">5+ years</option>
              <option value="10">10+ years</option>
            </select>

            <input
              type="text"
              placeholder="Filter by location..."
              value={selectedFilters.location}
              onChange={e => setSelectedFilters(f => ({ ...f, location: e.target.value }))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-xs focus:border-[#e8744f] focus:outline-none transition w-36"
            />

            <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-gray-200 rounded-xl text-xs hover:bg-orange-50 transition">
              <input
                type="checkbox"
                checked={selectedFilters.activelyLooking}
                onChange={e => setSelectedFilters(f => ({ ...f, activelyLooking: e.target.checked }))}
                className="w-3.5 h-3.5 accent-[#e8744f]"
              />
              Actively Looking
            </label>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 bg-red-50 text-red-500 rounded-xl text-xs hover:bg-red-100 transition"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {filteredPortfolios.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
            <Search size={40} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">No results found</h3>
            <p className="text-sm text-gray-400 mb-4">Try adjusting your search or filters</p>
            <button onClick={clearFilters} className="text-sm text-[#e8744f] hover:underline">Clear all filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPortfolios.map((portfolio) => (
              <div
                key={portfolio.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                onClick={(e) => handleViewProfile(e, portfolio)}
              >
                {/* Card Header */}
                <div className={`p-5 text-center ${
                  portfolio.theme === 'dark' ? 'bg-gradient-to-br from-gray-800 to-gray-900' :
                  portfolio.theme === 'creative' ? 'bg-gradient-to-br from-[#e8744f] to-[#d66440]' :
                  'bg-gradient-to-br from-orange-50 to-white'
                }`}>
                  <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-2 border-2 border-white/30">
                    {portfolio.avatar_url ? (
                      <img src={portfolio.avatar_url} alt={portfolio.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#e8744f] flex items-center justify-center text-white text-xl font-bold">
                        {portfolio.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  {portfolio.availability && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      portfolio.availability === 'Actively looking' ? 'bg-green-500/20 text-green-600' :
                      portfolio.availability === 'Not available' ? 'bg-red-500/20 text-red-500' :
                      'bg-orange-500/20 text-orange-600'
                    }`}>
                      • {portfolio.availability}
                    </span>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 truncate">{portfolio.name}</h3>
                  <p className="text-sm text-gray-500 truncate mb-2">{portfolio.title}</p>

                  {(portfolio.profession || portfolio.branch) && (
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 mb-2">
                      🎓 {[portfolio.branch, portfolio.profession].filter(Boolean).join(' · ')}
                    </span>
                  )}

                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                    {portfolio.location && (
                      <span className="flex items-center gap-1"><MapPin size={10} />{portfolio.location}</span>
                    )}
                    {portfolio.years_experience && (
                      <span className="flex items-center gap-1"><Clock size={10} />{portfolio.years_experience}+ yrs</span>
                    )}
                  </div>

                  {portfolio.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {portfolio.skills.slice(0, 3).map((skill, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{skill}</span>
                      ))}
                      {portfolio.skills.length > 3 && (
                        <span className="text-xs px-2 py-0.5 text-gray-400">+{portfolio.skills.length - 3}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Eye size={11} />{portfolio.views || 0} views
                    </span>
                    <button
                      className="text-xs text-[#e8744f] font-medium hover:underline"
                      onClick={e => { e.stopPropagation(); navigate(`/p/${portfolio.id}`); }}
                    >
                      View Profile →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}